import React, { useState, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  QrCode,
  Link,
  Mail,
  Copy,
  FileImage,
  FileText,
  Share2,
  AlertCircle,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "@/hooks/use-toast";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import MultiEmailInput from "./MultiEmailInput";
import AppLogo from "./AppLogo";
import { useAuth } from "@/hooks/useAuth";
import { apiClient } from "@/lib/api";

/**
 * Interface for content that can be shared via the ShareModal
 */
export interface ShareContent {
  /** Unique identifier for the content */
  id: string;
  /** Title of the content to be shared */
  title: string;
  /** URL to the content */
  url: string;
  /** Optional description of the content */
  description?: string;
  /** Type of content for better labeling and customization */
  type?: "campaign" | "report" | "profile" | "dashboard" | "custom";
}

/**
 * Props for the ShareModal component
 */
interface ShareModalProps {
  /** Content object containing information about what to share */
  content: ShareContent;
  /** Custom trigger element. If not provided, a default button will be used */
  children?: React.ReactNode;
  /** Custom email message template */
  defaultMessage?: string;
  /** Whether to show the email sharing tab */
  showEmailTab?: boolean;
  /** Whether to show the QR code tab */
  showQRTab?: boolean;
  /** Whether to show the link copying tab */
  showLinkTab?: boolean;
  /** Custom tabs to add to the modal */
  customTabs?: Array<{
    value: string;
    label: string;
    icon: React.ReactNode;
    content: React.ReactNode;
  }>;
  /** Callback function called when sharing actions are performed */
  onShare?: (method: string, data?: Record<string, unknown>) => void;
}

/**
 * A reusable ShareModal component that provides multiple sharing options
 *
 * @example
 * ```tsx
 * // Basic usage for sharing a campaign
 * <ShareModal
 *   content={{
 *     id: "campaign-123",
 *     title: "My Awesome Product",
 *     url: "https://example.com/campaign/123",
 *     type: "campaign"
 *   }}
 * >
 *   <Button>Share Campaign</Button>
 * </ShareModal>
 *
 * // Advanced usage with custom tabs and callbacks
 * <ShareModal
 *   content={{
 *     id: "report-456",
 *     title: "Analytics Report",
 *     url: "https://example.com/report/456",
 *     type: "report"
 *   }}
 *   showEmailTab={false}
 *   customTabs={[
 *     {
 *       value: "social",
 *       label: "Social Media",
 *       icon: <Twitter className="w-4 h-4" />,
 *       content: <SocialMediaShare />
 *     }
 *   ]}
 *   onShare={(method, data) => {
 *     console.log(`Shared via ${method}`, data);
 *   }}
 * />
 * ```
 */
const ShareModal = ({
  content,
  children,
  defaultMessage,
  showEmailTab = true,
  showQRTab = true,
  showLinkTab = true,
  customTabs = [],
  onShare,
}: ShareModalProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("link");
  const [emails, setEmails] = useState<string[]>([]);
  const [emailMessage, setEmailMessage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");

  const qrCodeRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated } = useAuth();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(content.url);
    toast({
      title: "Link copied! 📋",
      description: `${content.title} link has been copied to your clipboard.`,
    });
    onShare?.("link", { url: content.url });
  };

  const downloadQRAsImage = async () => {
    if (!qrCodeRef.current) return;

    setIsDownloading(true);
    try {
      const dataUrl = await toPng(qrCodeRef.current, {
        quality: 1.0,
        backgroundColor: "white",
      });

      const link = document.createElement("a");
      link.download = `${content.title.replace(/\s+/g, "_")}_QR.png`;
      link.href = dataUrl;
      link.click();

      toast({
        title: "QR Code downloaded! 📱",
        description: "QR code saved as image file.",
      });
      onShare?.("qr-image", { url: content.url, title: content.title });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download QR code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadQRAsPDF = async () => {
    if (!qrCodeRef.current) return;

    setIsDownloading(true);
    try {
      const dataUrl = await toPng(qrCodeRef.current, {
        quality: 1.0,
        backgroundColor: "white",
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 80;
      const imgHeight = 80;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Center the QR code
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2 - 20;

      pdf.addImage(dataUrl, "PNG", x, y, imgWidth, imgHeight);

      // Add content title and branding
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text(content.title, pageWidth / 2, y + imgHeight + 15, {
        align: "center",
      });

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      const typeLabel = content.type
        ? `${
            content.type.charAt(0).toUpperCase() + content.type.slice(1)
          } QR Code`
        : "QR Code";
      pdf.text(typeLabel, pageWidth / 2, y + imgHeight + 25, {
        align: "center",
      });
      pdf.text(content.url, pageWidth / 2, y + imgHeight + 35, {
        align: "center",
      });

      pdf.save(`${content.title.replace(/\s+/g, "_")}_QR.pdf`);

      toast({
        title: "QR Code downloaded! 📄",
        description: "QR code saved as PDF file.",
      });
      onShare?.("qr-pdf", { url: content.url, title: content.title });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Could not download QR code. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please log in to share campaigns via email.",
        variant: "destructive",
      });
      return;
    }

    if (emails.length === 0) {
      toast({
        title: "No emails entered",
        description: "Please enter at least one email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);
    setEmailStatus("sending");

    try {
      const response = await apiClient.post<{
        success: boolean;
        emailsSent: number;
        shareId: string;
      }>(`/email/share/${content.id}`, {
        recipientEmails: emails,
        message: emailMessage,
      });

      if (response.success) {
        setEmailStatus("success");
        toast({
          title: "Emails sent successfully! 📧",
          description: `Campaign shared with ${emails.length} recipient${
            emails.length !== 1 ? "s" : ""
          }.`,
        });

        onShare?.("email", { emails, message: emailMessage, content });

        // Reset form
        setEmails([]);
        setEmailMessage(getDefaultEmailMessage());

        // Close modal after a short delay
        setTimeout(() => {
          setOpen(false);
        }, 2000);
      }
    } catch (error: unknown) {
      setEmailStatus("error");
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to send emails. Please try again.";

      toast({
        title: "Failed to send emails",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getDefaultEmailMessage = () => {
    if (defaultMessage) return defaultMessage;

    const typeLabel =
      content.type === "campaign" ? "product" : content.type || "content";
    const actionText =
      content.type === "campaign"
        ? "validate this product idea and would love your feedback"
        : "share this with you";

    return `Hi there!

I wanted to share this exciting ${typeLabel} with you: "${content.title}"

${
  content.type === "campaign"
    ? "I'm currently validating this product idea and would love your feedback! It only takes a few minutes to participate in the survey."
    : ""
}

Check it out here: ${content.url}

${
  content.type === "campaign"
    ? "Your insights would be incredibly valuable in helping shape this product. I'd really appreciate if you could take a moment to share your thoughts!"
    : ""
}

Thanks so much,
${user?.name || "Your name"}`;
  };

  // Set default message when component mounts or content changes
  React.useEffect(() => {
    setEmailMessage(getDefaultEmailMessage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Determine which tabs to show
  const availableTabs = useMemo(() => {
    const tabs = [];

    if (showLinkTab) {
      tabs.push({
        value: "link",
        label: "Copy Link",
        icon: <Link className="w-4 h-4" />,
      });
    }

    if (showQRTab) {
      tabs.push({
        value: "qr",
        label: "QR Code",
        icon: <QrCode className="w-4 h-4" />,
      });
    }

    if (showEmailTab) {
      tabs.push({
        value: "email",
        label: "Bulk Email",
        icon: <Mail className="w-4 h-4" />,
      });
    }

    // Add custom tabs
    tabs.push(...customTabs);

    return tabs;
  }, [showLinkTab, showQRTab, showEmailTab, customTabs]);

  // Set initial active tab
  React.useEffect(() => {
    if (
      availableTabs.length > 0 &&
      !availableTabs.find((tab) => tab.value === activeTab)
    ) {
      setActiveTab(availableTabs[0].value);
    }
  }, [availableTabs, activeTab]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            variant="outline"
            className="flex items-center gap-2 border-2 border-primary-200 hover:border-primary-300 hover:bg-primary-50 px-8"
          >
            <Share2 className="size-5" />
            Share {content.type || "Content"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share "{content.title}"
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            className="grid w-full"
            style={{
              gridTemplateColumns: `repeat(${availableTabs.length}, 1fr)`,
            }}
          >
            {availableTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex items-center gap-2"
              >
                {tab.icon}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {showLinkTab && (
            <TabsContent value="link" className="space-y-4">
              <div className="space-y-3">
                <Label className="text-sm font-medium">URL</Label>
                <div className="flex items-center gap-2">
                  <Input value={content.url} readOnly className="flex-1" />
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Button
                      size="sm"
                      onClick={copyToClipboard}
                      className="shrink-0"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  </motion.div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Share this link with anyone to let them view this{" "}
                  {content.type || "content"}.
                </p>
              </div>
            </TabsContent>
          )}

          {showQRTab && (
            <TabsContent value="qr" className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <div
                  ref={qrCodeRef}
                  className="bg-white p-6 rounded-lg border-2 border-gray-200 flex flex-col items-center space-y-3 relative"
                >
                  <div className="relative">
                    <QRCodeSVG
                      value={content.url}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                    {/* App Icon Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <AppLogo size="md" />
                    </div>
                  </div>

                  <div className="text-center space-y-1">
                    <p className="font-semibold text-gray-900 text-lg">
                      {content.title}
                    </p>
                    <p className="text-sm text-gray-600">
                      {content.type
                        ? `${
                            content.type.charAt(0).toUpperCase() +
                            content.type.slice(1)
                          } QR Code`
                        : "QR Code"}
                    </p>
                    <p className="text-xs text-gray-500 break-all max-w-[200px]">
                      {content.url}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={downloadQRAsImage}
                    disabled={isDownloading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <FileImage className="w-4 h-4" />
                    {isDownloading ? "Downloading..." : "Download as Image"}
                  </Button>
                  <Button
                    onClick={downloadQRAsPDF}
                    disabled={isDownloading}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    {isDownloading ? "Downloading..." : "Download as PDF"}
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Scan this QR code with your phone to open the{" "}
                  {content.type || "content"} directly.
                </p>
              </div>
            </TabsContent>
          )}

          {showEmailTab && (
            <TabsContent value="email" className="space-y-4">
              {!isAuthenticated ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Login Required</h3>
                  <p className="text-muted-foreground mb-4">
                    You need to be logged in to share campaigns via email.
                  </p>
                  <Button onClick={() => setOpen(false)}>Got it</Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Email Addresses
                      </Label>
                      <MultiEmailInput
                        emails={emails}
                        onEmailsChange={setEmails}
                        placeholder="Enter email addresses..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-medium">
                        Message
                      </Label>
                      <Textarea
                        id="message"
                        placeholder="Add a personal message..."
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        className="min-h-32"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setEmailMessage(getDefaultEmailMessage())
                        }
                        className="text-xs"
                      >
                        Reset to default message
                      </Button>
                    </div>

                    <Button
                      onClick={handleBulkEmail}
                      disabled={emails.length === 0 || isSendingEmail}
                      className="w-full"
                    >
                      {isSendingEmail ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : emailStatus === "success" ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Sent Successfully!
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Send to {emails.length} recipient
                          {emails.length !== 1 ? "s" : ""}
                        </>
                      )}
                    </Button>

                    {emailStatus === "success" && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800">
                          <strong>Success!</strong> Your campaign has been
                          shared via email.
                        </p>
                      </div>
                    )}

                    {emailStatus === "error" && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-800">
                          <strong>Error:</strong> Failed to send emails. Please
                          try again.
                        </p>
                      </div>
                    )}

                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Email sharing is limited to
                        registered users. Each user has a monthly limit of
                        emails they can send.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          )}

          {/* Render custom tabs */}
          {customTabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value}>
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
